import { TableColumnFilter, TableFilter, TableFilterComparator } from '@economic/taco';
import { escapeSpecialChars } from '~/app/shared/util/utilities/escapeSpecialChars';

type Comparisons = 'like' | 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte';
type ArrayComparisons = 'in' | 'nin';
type Conditionals = 'and' | 'or';
const EmptyMarker = {};

export const EnumFilters = [TableFilterComparator.IsOneOf, TableFilterComparator.IsNoneOf];

export const NumberFilters = [
    TableFilterComparator.IsEqualTo,
    TableFilterComparator.IsNotEqualTo,
    TableFilterComparator.IsGreaterThan,
    TableFilterComparator.IsLessThan,
    TableFilterComparator.IsBetween,
    TableFilterComparator.IsLessThanOrEqualTo,
    TableFilterComparator.IsGreaterThanOrEqualTo,
];

export const DateFilters = NumberFilters;

// TODO remove once support for TableFilterComparator.DoesNotContain is added in backend (need negation operator, see EC-62033)
export const TextFilters = [
    TableFilterComparator.Contains,
    TableFilterComparator.IsEqualTo,
    TableFilterComparator.IsNotEqualTo,
    TableFilterComparator.IsEmpty,
    TableFilterComparator.IsNotEmpty,
];

function encode(value: unknown): string | undefined {
    if (value === null) {
        return undefined;
    }

    if (value === EmptyMarker) {
        return '$null:';
    }

    switch (typeof value) {
        case 'object':
            return value instanceof Date ? value.toISOString().split('T')[0] : undefined;
        case 'boolean':
            return value.toString();
        case 'number':
            return isNaN(value) ? undefined : value.toString();
        case 'string':
            return encodeURIComponent(escapeSpecialChars(value.trim()));
        case 'bigint':
        case 'symbol':
        case 'function':
        case 'undefined':
        default:
            return undefined;
    }
}

function compare(id: string, operator: Comparisons, value: unknown) {
    const escaped = encode(value);
    if (escaped === undefined || escaped.trim() === '') {
        return undefined;
    }

    return `${id}$${operator}:${escaped}`;
}

function compareMany(id: string, comparator: Comparisons, values: unknown[], conditional: Conditionals) {
    if (!Array.isArray(values) || values.length === 0) {
        return undefined;
    }

    const exprs = values.map(value => compare(id, comparator, value)).filter(expr => expr !== undefined);

    if (exprs.length === 0) {
        return undefined;
    }

    if (exprs.length === 1) {
        return exprs[0];
    }

    return `(${composeMany(exprs, conditional)})`;
}

function arrayCompare(id: string, operator: ArrayComparisons, values: unknown[]) {
    // TODO need this until array comparisons are fully supported (see EC-62034)
    const polyfillArrayComparison = true;

    if (polyfillArrayComparison) {
        switch (operator) {
            case 'in':
                return compareMany(id, 'eq', values, 'or');
            case 'nin':
                return compareMany(id, 'ne', values, 'and');
            default:
                return undefined;
        }
    }

    if (!Array.isArray(values)) {
        return undefined;
    }

    const escaped = values.map(encode).filter(x => x !== undefined);
    const escapedArray = `[${escaped.join(',')}]`;

    return `${id}$${operator}:${escapedArray}`;
}

function compose(expr1: string, operator: Conditionals, expr2: string) {
    return `${expr1}$${operator}:${expr2}`;
}

function composeMany(exprs: string[], operator: Conditionals) {
    return exprs.join(`$${operator}:`);
}

export function parseFilter(filter: TableColumnFilter): string | undefined {
    const {
        id,
        value: { comparator, value },
    } = filter as TableFilter;

    switch (comparator) {
        case TableFilterComparator.Contains:
            return compare(id, 'like', value);

        case TableFilterComparator.IsEmpty:
            return compare(id, 'eq', EmptyMarker);

        case TableFilterComparator.IsNotEmpty:
            return compare(id, 'ne', EmptyMarker);

        case TableFilterComparator.IsEqualTo:
            return compare(id, 'eq', value);

        case TableFilterComparator.IsNotEqualTo:
            return compare(id, 'ne', value);

        case TableFilterComparator.IsGreaterThan:
            return compare(id, 'gt', value);

        case TableFilterComparator.IsLessThan:
            return compare(id, 'lt', value);

        case TableFilterComparator.IsBetween:
            const expr1 = compare(id, 'gte', value?.[0]);
            const expr2 = compare(id, 'lte', value?.[1]);

            if (expr1 === undefined || expr2 === undefined) {
                return undefined;
            }

            return compose(expr1, 'and', expr2);

        case TableFilterComparator.IsLessThanOrEqualTo:
            return compare(id, 'lte', value);

        case TableFilterComparator.IsGreaterThanOrEqualTo:
            return compare(id, 'gte', value);

        case TableFilterComparator.IsOneOf:
            return arrayCompare(id, 'in', value);

        case TableFilterComparator.IsNoneOf:
            return arrayCompare(id, 'nin', value);

        case TableFilterComparator.HasAnyOf: // TODO We do not have support for array-to-array comparisons in the backend (see EC-62032)
        case TableFilterComparator.HasAllOf: // ...
        case TableFilterComparator.HasNoneOf: // ...
        case TableFilterComparator.DoesNotContain: // TODO We lack a negation operator, so cannot invert 'like' (see EC-62033)
        default: {
            return undefined;
        }
    }
}

export function parseSearch<T>(searchAccessorMap?: Extract<keyof T, string>[], search?: string): undefined | string {
    if (searchAccessorMap === undefined) {
        return undefined;
    }

    const exprs = searchAccessorMap.map(id => compare(id, 'like', search)).filter(x => x !== undefined);

    if (exprs.length === 0) {
        return undefined;
    }

    return composeMany(exprs, 'or');
}

export default function parseFilters<T>(
    filters: TableColumnFilter[],
    searchAccessorMap?: Extract<keyof T, string>[],
    search?: string
): undefined | string {
    const parsedFilters = filters.map(parseFilter).filter(x => x !== undefined);
    const parsedSearch = parseSearch(searchAccessorMap, search);

    if (parsedFilters.length === 0) {
        return parsedSearch;
    }

    const composedFilters = composeMany(parsedFilters, 'and');

    if (parsedSearch === undefined) {
        return composedFilters !== '' ? composedFilters : undefined;
    }

    return compose(composedFilters, 'and', `(${parsedSearch})`);
}
